import { useMemo, useRef, useState } from 'react';
import { useModalA11y } from '../hooks/useModalA11y';
import {
  buildSportHandoffReport,
  exportSportHandoffToWord,
} from '../services/sportHandoffReport';

export default function SportHandoffReportModal({ client, onClose, onEditProfile }) {
  const report = useMemo(() => buildSportHandoffReport(client), [client]);
  const [anissaNote, setAnissaNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const panelRef = useRef(null);
  useModalA11y({ onClose, busy: exporting, panelRef });

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      await exportSportHandoffToWord(report, { confirmed, anissaNote });
    } catch (exportError) {
      setError(exportError?.message || 'Le rapport ne peut pas être exporté.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sport-handoff-title"
      className="jrn-brief-overlay"
      onClick={onClose}
    >
      <div ref={panelRef} className="jrn-brief-panel sport-handoff" onClick={(event) => event.stopPropagation()}>
        <header className="jrn-brief__header">
          <div>
            <p className="jrn-brief__eyebrow">{report.coordinationPlanned ? 'Coordination Anissa · Benoît' : 'Relais sport ponctuel'}</p>
            <h2 id="sport-handoff-title" className="jrn-brief__title">Relais sport</h2>
            <p className="jrn-brief__meta">{report.clientName} · Brouillon factuel</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" className="jrn-btn jrn-btn--soft">Fermer</button>
        </header>

        <div className="jrn-brief__body">
          <div className="sport-handoff__boundary">
            <strong>Ce rapport ne décide rien à la place d’un professionnel.</strong>
            <p>Il reprend uniquement les informations déclarées dans le dossier. Aucun diagnostic, résultat biologique, traitement ou note privée n’est inclus automatiquement.</p>
          </div>

          <dl className="sport-handoff__summary">
            <div><dt>Contexte</dt><dd>{report.contextLabel}</dd></div>
            <div><dt>Pack</dt><dd>{report.packLabel}</dd></div>
            <div><dt>Âge</dt><dd>{Number.isInteger(report.age) ? `${report.age} ans` : 'Non renseigné'}</dd></div>
          </dl>

          {!report.coordinationPlanned && (
            <div className="sport-handoff__context-notice" role="status">
              <strong>Relais ponctuel, déclenché par Anissa</strong>
              <p>{report.contextNotice}</p>
            </div>
          )}

          {!report.ready && (
            <div className="sport-handoff__missing" role="status">
              <strong>Dossier sport à compléter avant l’export</strong>
              <ul>{report.missing.map((label) => <li key={label}>{label}</li>)}</ul>
              {onEditProfile && (
                <button type="button" className="jrn-btn jrn-btn--secondary" onClick={() => { onClose(); onEditProfile(); }}>
                  Compléter le profil
                </button>
              )}
            </div>
          )}

          {report.sections.map((section, index) => (
            <section key={section.key} className="jrn-brief-section" style={{ '--brief-i': index }}>
              <div className="jrn-brief-section__head">
                <h3 className="jrn-brief-section__label">{section.title}</h3>
                <span className="jrn-brief-section__rule" aria-hidden="true" />
              </div>
              {section.rows.length > 0 ? (
                <dl className="sport-handoff__fields">
                  {section.rows.map((entry) => (
                    <div key={entry.label}>
                      <dt>{entry.label}</dt>
                      <dd>{entry.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : <p className="sport-handoff__empty">Aucune information renseignée.</p>}
            </section>
          ))}

          <section className="jrn-brief-section" style={{ '--brief-i': report.sections.length }}>
            <div className="jrn-brief-section__head">
              <h3 className="jrn-brief-section__label">Note de relais d’Anissa</h3>
              <span className="jrn-brief-section__rule" aria-hidden="true" />
            </div>
            <label className="sport-handoff__note">
              <span>Facultatif · ajouter uniquement ce qui est nécessaire à la prise en charge sportive</span>
              <textarea
                rows="5"
                value={anissaNote}
                onChange={(event) => { setAnissaNote(event.target.value); setConfirmed(false); }}
                placeholder="Informations pratiques ou points de coordination validés par Anissa…"
              />
            </label>
          </section>

          <label className="sport-handoff__confirm">
            <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
            <span>J’ai relu ce rapport et je confirme les informations destinées à Benoît.</span>
          </label>

          {error && <p className="sport-handoff__error" role="alert">{error}</p>}

          <div className="sport-handoff__actions">
            <button type="button" className="jrn-btn jrn-btn--primary" disabled={!report.ready || !confirmed || exporting} onClick={handleExport}>
              {exporting ? 'Création du Word…' : 'Exporter le rapport Word'}
            </button>
            <p>Aucun envoi automatique. Anissa garde la main sur le fichier.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
